/**
 * The return-to through sign-in for a portal link, and for nothing else
 * (docs/integration-portal-link-proposal.md §4.4 step 4 in the SVN repo).
 *
 * Someone who opens /#/portal/{id} while signed out goes through the login page; after it they
 * should land on the portal, not the dashboard. The path is kept in sessionStorage, so it belongs to
 * that one tab and dies with it. Only a /portal/<id> path is ever written or handed back, so this
 * can never become a general redirect.
 */
export const PORTAL_RETURN_KEY = "inistate.portalReturn";

const PORTAL_PATH = /^\/portal\/[A-Za-z0-9_-]{1,64}$/;

function storage() {
  try {
    return typeof window !== "undefined" && window.sessionStorage ? window.sessionStorage : null;
  } catch (err) {
    // Blocked storage (privacy mode, a sandboxed frame) just means no return-to.
    return null;
  }
}

/** The route path for a portal id, or null for an id that cannot be one. */
export function portalPath(id) {
  const value = "/portal/" + String(id == null ? "" : id);
  return PORTAL_PATH.test(value) ? value : null;
}

/** Remember to come back to this portal after signing in. False when nothing was kept. */
export function rememberPortalReturn(id) {
  const value = portalPath(id);
  const store = storage();
  if (!value || !store) return false;
  try {
    store.setItem(PORTAL_RETURN_KEY, value);
    return true;
  } catch (err) {
    return false;
  }
}

/** Drop the return-to: the portal answered, one way or the other. */
export function forgetPortalReturn() {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(PORTAL_RETURN_KEY);
  } catch (err) {
    /* nothing to forget */
  }
}

/** The kept portal path, consumed (read once, then removed); null when there is none or it is not one. */
export function takePortalReturn() {
  const store = storage();
  if (!store) return null;
  let value = null;
  try {
    value = store.getItem(PORTAL_RETURN_KEY);
    store.removeItem(PORTAL_RETURN_KEY);
  } catch (err) {
    return null;
  }
  return typeof value === "string" && PORTAL_PATH.test(value) ? value : null;
}

export default { PORTAL_RETURN_KEY, portalPath, rememberPortalReturn, forgetPortalReturn, takePortalReturn };
